import { Observable, map } from 'rxjs';
import { DataSourceAdapter } from './data-source-adapter.interface';
import { PaginatedDataService } from '../../../services/paginated-data.service';

/**
 * Adapter for server-side paginated data.
 * **Recommended for large datasets (100+ rows).**
 *
 * Delegates pagination, sorting, and filtering to PaginatedDataService,
 * which handles API calls and state management.
 *
 * ## Key Behaviors
 * - `paginate()` → calls `service.changePage()`
 * - `sort()` → calls `service.changeSort()`
 * - `filter()` → calls `service.search()`
 * - `totalCount$` → exposes total for paginator "X of Y" display
 * - `pageIndex$` / `pageSize$` → derived from `paginationState$` (optional consumers)
 *
 * ## Usage
 * ```ts
 * // 1. Provide service at component level
 * @Component({
 *   providers: [PaginatedDataService]
 * })
 * export class MySummaryComponent {
 *   dataService = inject(PaginatedDataService<MyRow, MySearchCriteria>);
 *
 *   constructor() {
 *     // 2. Configure the fetch function
 *     this.dataService.configure(
 *       (pagination, criteria) => this.api.search(pagination, criteria),
 *       { pageSize: 10, sortField: 'name', sortDirection: 'asc' }
 *     );
 *   }
 *
 *   onSearch(text: string) {
 *     this.dataService.search(text);
 *   }
 * }
 *
 * // 3. Bind to template (adapter created automatically)
 * <app-data-table [dataSource]="dataService" [config]="config" />
 * ```
 *
 * @template T - Row data type
 * @see PaginatedDataService
 */
export class PaginatedDataSourceAdapter<T> implements DataSourceAdapter<T> {
  readonly type = 'paginated' as const;

  constructor(private readonly service: PaginatedDataService<T, any>) {}

  connect(): Observable<T[]> {
    return this.service.data$ as Observable<T[]>;
  }

  disconnect(): void {
    // No cleanup needed - PaginatedDataService manages its own lifecycle
  }

  get totalCount$(): Observable<number> {
    return this.service.totalCount$ as Observable<number>;
  }

  get loading$(): Observable<boolean> {
    return this.service.loading$ as Observable<boolean>;
  }

  get pageIndex$(): Observable<number> | undefined {
    return this.service.paginationState$.pipe(
      map(state => {
        const pageSize = state.toIndex - state.fromIndex;
        return pageSize > 0 ? Math.floor(state.fromIndex / pageSize) : 0;
      })
    );
  }

  get pageSize$(): Observable<number> | undefined {
    return this.service.paginationState$.pipe(
      map(state => state.toIndex - state.fromIndex)
    );
  }

  paginate(page: number, pageSize: number): void {
    this.service.changePage(page, pageSize);
  }

  sort(field: string, direction: 'asc' | 'desc'): void {
    this.service.changeSort(field, direction);
  }

  filter(criteria: any): void {
    this.service.search(criteria);
  }
}
